import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { View as RNView, ScrollView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { Button } from '@shared/components/Button';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { BatchEditContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { MBChatInput } from '@shared/scenes/chat/input/containers/input';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { EntityForm } from '@shared/scenes/entity/components/EntityForm';
import { CardSummaryProvider } from '@shared/scenes/entity/components/EntityForm/CardSummaryProvider';
import { FormErrors } from '@shared/scenes/entity/components/FormErrors';
import { RemoteChangesWarningBanner } from '@shared/scenes/entity/components/RemoteChangesWarningBanner';
import { SubformNavigationContext } from '@shared/scenes/entity/components/fields/Subform/SubformEditField';
import { FieldConflictContext, useFormScrollContext } from '@shared/scenes/entity/context';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { grid } from '@shared/styles';
import { Colors } from '@shared/styles/colors';
import { API } from '@shared/types/api';
import { useIsDesktop, useIsMobile } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { ThreadContainer } from './ThreadContainer';

interface Props {
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  entityId: string;
  threadId?: string;
  channelSlug?: string;
  channelId?: number;
  groups?: API.ChannelGroupFragment[];
  style?: StyleProp<ViewStyle>;
  isSidebarOpen?: boolean;
  closeSidebar?: () => void;
  saveAttempted?: boolean;
  conflictingComponentIds?: string[];
  revertConflicts?: () => void;
  showRemoteChangesWarning?: boolean;
  onDismissRemoteChangesWarning?: () => void;
  onRevert?: () => void;
}

export function CardEntityEditForm({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  threadId,
  channelId,
  channelSlug,
  groups,
  style = {},
  isSidebarOpen,
  closeSidebar,
  saveAttempted,
  conflictingComponentIds = [],
  revertConflicts,
  showRemoteChangesWarning: externalShowWarning,
}: Props) {
  const isDesktop = useIsDesktop();

  const [isConflictBannerDismissed, setIsConflictBannerDismissed] = useState(false);
  const prevConflictCountRef = useRef(0);
  useEffect(() => {
    const currentCount = conflictingComponentIds.length;
    if (currentCount > prevConflictCountRef.current) {
      setIsConflictBannerDismissed(false);
    }
    prevConflictCountRef.current = currentCount;
  }, [conflictingComponentIds.length]);

  const showRemoteChangesWarning =
    externalShowWarning ?? (!isEmpty(conflictingComponentIds) && !isConflictBannerDismissed);

  const sharedProps = {
    components,
    fields,
    updateField,
    errors,
    workflowTemplateId,
    viewTemplateId,
    entityId,
    threadId,
    channelId,
    channelSlug,
    groups,
    style,
    saveAttempted,
    showRemoteChangesWarning,
    onDismissRemoteChangesWarning: () => setIsConflictBannerDismissed(true),
    onRevert: revertConflicts,
  };

  return (
    <FieldConflictContext.Provider value={conflictingComponentIds}>
      <CardSummaryProvider>
        {isDesktop ? (
          <FormWithChatSidebar
            {...sharedProps}
            isSidebarOpen={isSidebarOpen}
            closeSidebar={closeSidebar}
          />
        ) : (
          <FormWithBottomChat {...sharedProps} />
        )}
      </CardSummaryProvider>
    </FieldConflictContext.Provider>
  );
}

function FormWithBottomChat({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  threadId,
  channelSlug,
  channelId,
  groups,
  saveAttempted,
  showRemoteChangesWarning,
  onDismissRemoteChangesWarning,
  onRevert,
}: Props) {
  const { isComponentFocused } = useComponentFocus();
  const scrollView = useFormScrollContext();
  const scrollToEnd = useCallback(() => {
    scrollView?.current?.scrollToEnd();
  }, [scrollView]);

  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();

  const showChat = !isNil(threadId) && !isNil(channelId) && !isNil(groups) && !isNil(channelSlug);
  const showChatInput = showChat && (isDesktop || !isComponentFocused);

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return null;
  }

  return (
    <CardFormTypeContext.Provider value="EDIT">
      <BatchEditContextProvider>
        <SubformNavigationContext
          fields={fields}
          updateField={updateField}
          saveAttempted={saveAttempted}
          apiErrors={errors}
          entityId={entityId}
        >
          <FormErrors errors={errors} />
          {showRemoteChangesWarning && (
            <RemoteChangesWarningBanner
              header={i18n('card.update.remoteConflictHeader')}
              subheader={i18n('card.update.remoteConflictSubheader')}
              onDismiss={onDismissRemoteChangesWarning ?? (() => {})}
              onRevert={onRevert}
            />
          )}
          <ScrollView
            ref={scrollView}
            style={styles.formWithBottomChatScrollView}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={isDesktop ? 'none' : 'on-drag'}
            contentContainerStyle={{ flexGrow: 1 }}
          >
            <EntityForm
              fields={fields}
              updateField={updateField}
              components={components}
              errors={errors}
              workflowTemplateId={workflowTemplateId}
              viewTemplateId={viewTemplateId}
              entityId={entityId}
            />
            {showChat && <ThreadContainer threadId={threadId} channelSlug={channelSlug} />}
          </ScrollView>
          {showChatInput && (
            <RNView
              style={[
                styles.webContent,
                isMobile && styles.mobileWebContent,
                styles.bottomChatInput,
              ]}
            >
              <MBChatInput
                placeholder={i18n('chat.input.placeholder')}
                draftId={`thread:${threadId}`}
                channelId={channelId}
                groups={groups}
                onSend={scrollToEnd}
                autoFocus={false}
              />
            </RNView>
          )}
        </SubformNavigationContext>
      </BatchEditContextProvider>
    </CardFormTypeContext.Provider>
  );
}

function FormWithChatSidebar({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  threadId,
  channelSlug,
  channelId,
  groups,
  isSidebarOpen,
  closeSidebar,
  saveAttempted,
  showRemoteChangesWarning,
  onDismissRemoteChangesWarning,
  onRevert,
}: Props) {
  const scrollView = useFormScrollContext();
  const scrollToEnd = useCallback(() => {
    scrollView?.current?.scrollToEnd();
  }, [scrollView]);

  const debouncedScrollToEnd = useCallback(() => {
    setTimeout(scrollToEnd, 100);
    setTimeout(scrollToEnd, 1000);
  }, [scrollToEnd]);

  const isDesktop = useIsDesktop();
  const showForm = isDesktop || !isSidebarOpen;
  const chatEnabled =
    !isNil(threadId) && !isNil(channelId) && !isNil(groups) && !isNil(channelSlug);
  const showChat = (isDesktop && isSidebarOpen) || isSidebarOpen;

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return null;
  }

  return (
    <CardFormTypeContext.Provider value="EDIT">
      <BatchEditContextProvider>
        <SubformNavigationContext
          fields={fields}
          updateField={updateField}
          saveAttempted={saveAttempted}
          apiErrors={errors}
          entityId={entityId}
        >
          <RNView style={styles.panes}>
            {showForm && (
              <RNView style={styles.formPane}>
                <FormErrors errors={errors} />
                {showRemoteChangesWarning && (
                  <RemoteChangesWarningBanner
                    header={i18n('card.update.remoteConflictHeader')}
                    subheader={i18n('card.update.remoteConflictSubheader')}
                    onDismiss={onDismissRemoteChangesWarning ?? (() => {})}
                    onRevert={onRevert}
                  />
                )}
                <ScrollView
                  style={{ width: '100%' }}
                  keyboardShouldPersistTaps="handled"
                  keyboardDismissMode={isDesktop ? 'none' : 'on-drag'}
                  contentContainerStyle={{ flexGrow: 1 }}
                >
                  <EntityForm
                    fields={fields}
                    updateField={updateField}
                    components={components}
                    errors={errors}
                    workflowTemplateId={workflowTemplateId}
                    viewTemplateId={viewTemplateId}
                    entityId={entityId}
                  />
                </ScrollView>
              </RNView>
            )}
            {chatEnabled && (
              <RNView
                style={[
                  isDesktop && styles.rightPane,
                  !showChat && styles.rightPaneHidden,
                  !isDesktop && styles.chatPaneFullWidth,
                ]}
                testID="card-chat-pane"
              >
                {isDesktop && (
                  <MBView row centerVertically style={styles.rightPaneHeader}>
                    <Text size="body1" weight="semibold">
                      {i18n('card.rightPane.chat.title')}
                    </Text>
                    <Button
                      variant="ghost"
                      icon="exit"
                      iconOnly
                      size="medium"
                      iconSize={grid(3)}
                      onClick={closeSidebar}
                    />
                  </MBView>
                )}
                <ScrollView
                  ref={scrollView}
                  style={styles.chatThreadScrollView}
                  keyboardShouldPersistTaps="handled"
                >
                  <ThreadContainer
                    threadId={threadId}
                    channelSlug={channelSlug}
                    onThreadUpdated={debouncedScrollToEnd}
                    showHeader={!isDesktop}
                  />
                </ScrollView>
                <MBChatInput
                  placeholder={i18n('chat.input.placeholder')}
                  draftId={`thread:${threadId}`}
                  channelId={channelId}
                  groups={groups}
                  autoFocus={false}
                  onSend={scrollToEnd}
                  paddingHorizontal={1.5}
                />
              </RNView>
            )}
          </RNView>
        </SubformNavigationContext>
      </BatchEditContextProvider>
    </CardFormTypeContext.Provider>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingTop: grid(1),
    paddingBottom: grid(8),
  },
  webContent: {
    width: '100%',
    paddingHorizontal: grid(4),
    alignSelf: 'center',
  },
  mobileWebContent: {
    paddingHorizontal: 0,
  },
  panes: {
    flexDirection: 'row',
    flex: 1,
  },
  formPane: {
    flex: 1,
    minWidth: 420,
    backgroundColor: Colors.v2.backgroundGrayLight,
  },
  formWithBottomChatScrollView: {
    width: '100%',
    backgroundColor: Colors.v2.backgroundGrayLight,
  },
  rightPane: {
    maxWidth: 400,
    flex: 1,
    borderLeftWidth: 1,
    borderColor: Colors.v2.gray10,
    backgroundColor: Colors.v2.white,
  },
  rightPaneHidden: {
    display: 'none',
  },
  rightPaneHeader: {
    paddingHorizontal: grid(2),
    paddingVertical: grid(1),
    borderBottomWidth: 1,
    borderColor: Colors.v2.gray10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rightPaneHeaderArrowIcon: {
    paddingHorizontal: grid(1.25),
    paddingTop: grid(0.5),
  },
  chatPaneFullWidth: {
    flex: 1,
  },
  chatThreadScrollView: {
    flex: 1,
    paddingRight: grid(1),
    borderBottomColor: Colors.v2.gray10,
    borderBottomWidth: 1,
  },
  bottomChatInput: {
    borderTopWidth: 1,
    borderColor: Colors.v2.gray10,
  },
});
